"use client"

/* ds-typo-ignore-file — EN-only prototype ad LP: intentional sub-DS sizes in the immersive scroll hero scrim/KPI labels (not shipped DS surfaces) */

import { useCountry } from "@/components/layout/country-provider"
import { bloodPriceArg } from "@/lib/blood-price-arg"
import { useLivePrices } from "@/lib/use-live-prices"
import Image from "next/image"
import { useState } from "react"
import { useTranslations } from "@/lib/intl/use-translations"
import { goKeys as k } from "@/i18n/go-keys"
import { blobMediaUrl } from "@/lib/blob-url"
import { ImmuneStepsTimeline } from "@/components/ui/immune-steps-timeline"
import { BiomarkerDiseaseExplorer } from "@/components/ui/biomarker-disease-explorer"
import { baseBloodTest } from "@/lib/base-blood-test"
import { GoogleRating } from "@/components/ui/google-rating"
import { Button, ArrowRightIcon } from "@/components/ui/button"
import { HeroTrustStrip } from "@/components/ui/hero-trust-strip"
import { PartnerLogoMarquee } from "@/components/ui/partner-logo-marquee"
import { homeKeys as hk } from "@/i18n/home-keys"
import type { ReviewerExpert } from "@/lib/reviewer-experts"
import { DoctorsSection } from "../../../_components/doctors-section"
import { TestimonialsSection } from "../../../_components/testimonials-section"
import type { Testimonial } from "@/lib/cms"
import { MARKERS, PROCESS } from "./metabolism-data"
import {
  SECTION,
  BookingLink,
  MeasureMarkerCard,
  CtaLabel,
  CtaReassurance,
  useBookingHref,
} from "./metabolism-components"
import {
  ImmersiveFullPicture,
  MetabolismMriImmersive,
} from "./metabolism-immersive"
import {
  MetabolismSigns,
  MetabolismReport,
  MetabolismGpCompare,
  MetabolismPricing,
  MetabolismFaq,
  MetabolismClosingCta,
} from "./metabolism-sections"

export default function MetabolismClient({
  bloodDrawCount,
  experts,
  testimonials,
}: {
  /** Live count of active blood-draw labs (CMS `locations` where type === "blood"). */
  bloodDrawCount: number
  /** Hand-picked diagnostics/blood advisors (CMS reviewers), in display order. */
  experts: ReviewerExpert[]
  /** CMS testimonials for the reviews carousel (empty → section self-hides). */
  testimonials: Testimonial[]
}) {
  const t = useTranslations("go")
  const { code: country } = useCountry()
  const prices = useLivePrices(country)
  const ht = useTranslations("home")
  // Active cross-marker insight reflected in the readout panel (section 4).
  const [activeInsight, setActiveInsight] = useState(0)

  const bookingHref = useBookingHref()

  // Step 1 advertises the live number of blood-draw labs from the CMS. The copy
  // stays in the message catalog with a non-ICU `[[count]]` sentinel (a real
  // `{count}` placeholder would force the shared "go" namespace to require
  // interpolation args on every generic t() call), interpolated here.
  const processSteps = PROCESS.map((s, i) =>
    i === 0
      ? {
          ...s,
          desc: t(k.metabolismProcessStep1Desc).replace(
            "[[count]]",
            String(bloodDrawCount)
          ),
        }
      : s
  )

  return (
    <>
      {/* ── 1. Hero — full-bleed video with overlaid copy (style ported 1:1 from /go/immunity) ── */}
      {/* NOTE: hero imagery is a prototype placeholder (hiker / everyday energy) — swap for final metabolism art before launch. */}
      <section className="relative isolate flex min-h-[calc(80vh+40px)] items-center overflow-hidden bg-neutral-900 text-white">
        {/* full-bleed cover image: subject is weighted right, the misty water on
            the left keeps the headline area clear. On mobile center-crop pushes
            the subject out of frame, so bias the crop rightward on small screens. */}
        <Image
          src={blobMediaUrl("/images/metabolism/hero-metabolism-hike.webp")}
          alt=""
          fill
          priority
          // See the sibling comment in `immunity-client.tsx`: as of Next 16
          // `priority` emits the preload link but not `fetchpriority`, so the
          // LCP image queued at Low priority without this.
          fetchPriority="high"
          sizes="100vw"
          className="object-cover object-[68%_center] md:object-center"
          aria-hidden
        />
        {/* top scrim for the transparent navbar */}
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-b from-black/55 via-transparent to-transparent"
        />
        {/* legibility scrim: bottom-up on mobile, left-to-right on desktop */}
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/35 to-black/10 md:bg-gradient-to-r md:from-black/72 md:via-black/22 md:to-transparent"
        />
        <div className={`${SECTION} relative z-10 w-full`}>
          <div className="flex max-w-[636px] flex-col items-start gap-6 py-28">
            <p className="type-label-md text-white/60 uppercase">
              {t(k.metabolismHeroEyebrow)}
            </p>
            <h1 className="type-display-md md:type-display-lg leading-[0.98] text-white">
              {t(k.metabolismHeroHeading)}
            </h1>
            <p className="type-body-lg max-w-[480px] text-white/85">
              {t(k.metabolismHeroBody)}
            </p>
            <GoogleRating
              variant="onDark"
              staticOnDark
              rating=""
              reviewLabel={t(k.metabolismHeroReviewLabel)}
              className="self-start"
            />
            <BookingLink
              href={bookingHref}
              location="metabolism-hero"
              className="block w-full sm:inline-block sm:w-auto"
            >
              <Button
                size="lg"
                variant="primary"
                className="group w-full sm:w-auto"
                iconRight={
                  <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
                }
              >
                <CtaLabel
                  label={t(k.metabolismHeroCta1, {
                    price: bloodPriceArg(prices),
                  })}
                />
              </Button>
            </BookingLink>
          </div>
        </div>
        {/* trust strip — same component as the membership hero, left-aligned */}
        <HeroTrustStrip
          align="left"
          compactMobile
          className="pb-4 md:pb-5"
          items={[
            {
              value: t(k.metabolismHeroKpi1Value),
              label: t(k.metabolismHeroKpi1Label),
            },
            {
              value: t(k.metabolismHeroKpi2Value),
              label: t(k.metabolismHeroKpi2Label),
            },
            {
              value: t(k.metabolismHeroKpi3Value),
              label: t(k.metabolismHeroKpi3Label),
            },
          ]}
        />
      </section>

      {/* ── 2. Partner logos — a fixed, curated row (no marquee) ── */}
      <div className="mx-auto max-w-[1100px] px-6 pt-4 pb-6 md:px-10 md:pt-8">
        <PartnerLogoMarquee
          layout="static"
          only={[
            "Unilabs",
            "Oxford",
            "Google",
            "Siemens Healthineers",
            "TUM",
            "tech.eu",
          ]}
        />
      </div>

      {/* ── 3. Signs — symptom cards + "stop guessing" wedge (extracted) ── */}
      <MetabolismSigns />

      {/* ── 4. The full picture — immersive scroll-blur ── */}
      <section>
        <div className={SECTION}>
          <ImmersiveFullPicture
            activeIndex={activeInsight}
            onPick={setActiveInsight}
          />
        </div>
      </section>

      {/* ── 5. What we measure — the four groups, in the health-risks card design
          (photo tile + icon · category · title · description · biomarker chips). ── */}
      <section className="bg-white py-16 pb-28 md:py-24 md:pb-32">
        <div className={SECTION}>
          <div className="mx-auto flex max-w-[677px] flex-col items-center gap-4 text-center">
            <p className="type-label-md text-neutral-600 uppercase">
              {t(k.metabolismMeasureEyebrow)}
            </p>
            <h2 className="type-display-md text-neutral-900">
              {t(k.metabolismMeasureHeading)}
            </h2>
            <p className="type-body-xl max-w-[520px] text-neutral-500">
              {t(k.metabolismMeasureBody)}
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {MARKERS.map((m) => (
              <MeasureMarkerCard key={m.titleKey} marker={m} t={t} />
            ))}
          </div>

          <div className="mt-14 flex flex-col items-center gap-6">
            <BookingLink
              href={bookingHref}
              location="metabolism-measure"
              className="block w-full sm:inline-block sm:w-auto"
            >
              <Button
                size="lg"
                variant="primary"
                className="group w-full sm:w-auto"
                iconRight={
                  <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
                }
              >
                <CtaLabel
                  label={t(k.metabolismHeroCta1, {
                    price: bloodPriceArg(prices),
                  })}
                />
              </Button>
            </BookingLink>
            <CtaReassurance />
          </div>
        </div>
      </section>

      {/* ── 7b. Where the risk is stored — the "see the cause" MRI beat,
          immersive pinned-scroll variant (text rides in, then the imaging cards) ── */}
      <MetabolismMriImmersive />

      {/* ── 7c. Bridge: one draw, your whole body (coverage explorer). Sits after
          the MRI beat so the imaging arm comes before the deep blood dive. ── */}
      <section className="bg-neutral-50 py-16 md:py-24">
        <div className={SECTION}>
          {/* Centered header — matches the Advanced Blood Test explorer header */}
          <div className="mx-auto flex max-w-[720px] flex-col items-center gap-3 text-center md:gap-4">
            <p className="type-label-md text-neutral-600 uppercase">
              {t(k.metabolismCoverageEyebrow)}
            </p>
            <h2 className="type-display-md text-neutral-900">
              {t(k.metabolismCoverageHeading)}
            </h2>
            <p className="type-body-lg max-w-[520px] text-neutral-600">
              {t(k.metabolismCoverageBody)}
            </p>
          </div>
          <div className="mt-12">
            {/* Live CMS-backed explorer (locale-aware) — biomarker-only, same
                component the real /biomarkers page + sibling /go LPs use. The
                former static snapshot rendered English-only regardless of
                locale (AHD-8117). `hideHeader` suppresses its own duplicate
                header + internal padding so it aligns with this section. */}
            <BiomarkerDiseaseExplorer
              packages={[baseBloodTest(country).biomarkerPackage]}
              tabsAllowed={["biomarkers"]}
              defaultCategorySlug="metabolic"
              hideHeader
            />
          </div>
          <div className="mt-10 flex flex-col items-center gap-3">
            <BookingLink
              href={bookingHref}
              location="metabolism-coverage"
              className="block w-full sm:inline-block sm:w-auto"
            >
              <Button
                size="lg"
                variant="primary"
                className="group w-full sm:w-auto"
                iconRight={
                  <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
                }
              >
                <CtaLabel
                  label={t(k.metabolismCoverageCta, {
                    price: bloodPriceArg(prices),
                  })}
                />
              </Button>
            </BookingLink>
          </div>
        </div>
      </section>

      {/* ── 8. How it works (scroll-linked stepper, concept from PR #1309) ── */}
      <ImmuneStepsTimeline
        label={t(k.metabolismProcessLabel)}
        headingKey={k.metabolismProcessHeading}
        bodyKey={k.metabolismProcessBody}
        stepWord={t(k.metabolismProcessStepWord)}
        steps={processSteps}
        namespace="go"
        cta={
          <div className="flex flex-col items-center gap-6">
            <BookingLink
              href={bookingHref}
              location="metabolism-how-it-works"
              className="block w-full sm:inline-block sm:w-auto"
            >
              <Button
                size="lg"
                variant="primary"
                className="group w-full sm:w-auto"
                iconRight={
                  <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
                }
              >
                <CtaLabel
                  label={t(k.metabolismHeroCta1, {
                    price: bloodPriceArg(prices),
                  })}
                />
              </Button>
            </BookingLink>
            <CtaReassurance />
          </div>
        }
      />

      {/* ── 7d. Your report ── */}
      <MetabolismReport />

      {/* ── 9b. Testimonials carousel (CMS-backed; self-hides if none) — social
          proof right before the offer. ── */}
      {/* Shorter video tiles (4/5 vs the default 9/16) crop the rail ~30% — the
          text cards self-stretch to match, so the whole section shrinks. */}
      <TestimonialsSection
        cmsTestimonials={testimonials}
        videoAspectClass="aspect-[4/5]"
      />

      {/* ── 9c. Why not just my GP? — compact ordinary-vs-Ahead comparison
          table (reuses the "How Ahead compares" design). ── */}
      <MetabolismGpCompare />

      {/* ── 10. The offer — blood vs blood+MRI ── */}
      <MetabolismPricing />

      {/* ── 11. Experts — shared DoctorsSection used exactly like the homepage
          (columnHeading + photo grid), minus the MRI-framed count stat. Experts
          are the diagnostics/blood-leaning advisors hand-picked in page.tsx. ── */}
      <DoctorsSection
        initialExperts={experts}
        showCount={false}
        columnHeading={
          <h2 className="type-display-md max-w-md text-neutral-900">
            {ht(hk.expertsHeading)}
          </h2>
        }
      />

      {/* ── 12. FAQ — same set + layout as /go/bloodtest (home namespace) ── */}
      <MetabolismFaq />

      {/* ── 13. Closing CTA ── */}
      <MetabolismClosingCta />
    </>
  )
}
